import React, { useCallback, useState } from 'react';

import { projectService } from '../services/projectService';
import type { SupervisorButton } from '../types/supervisorButtons';

const KEEP_CURRENT_TITLE = 'Close the updates that did not finish and keep those artifacts as they are. '
  + 'This change will not reach them.';

interface ArtifactUpdatesKeepCurrentProps {
  projectId?: string | null;
  entryId?: string;
  taskIds: string[];
  enabled: boolean;
  variant: 'overview' | 'chat';
}

/** Only the updates that failed can be kept; the ones waiting on them are offered again. */
export function keepCurrentTaskIds(button: SupervisorButton): string[] {
  const ids = button.payload?.keep_current_task_ids;

  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

/** The way past updates that keep failing: close them and leave their artifacts unchanged. */
export const ArtifactUpdatesKeepCurrent: React.FC<ArtifactUpdatesKeepCurrentProps> = ({
  projectId, entryId, taskIds, enabled, variant,
}) => {
  const [state, setState] = useState<'idle' | 'pending' | 'kept' | 'error'>('idle');
  const keep = useCallback(async () => {
    if (!projectId || !entryId || !taskIds.length || state === 'pending' || state === 'kept') return;
    setState('pending');
    try {
      await projectService.keepCurrentArtifacts(projectId, entryId, taskIds);
      setState('kept');
    } catch {
      setState('error');
    }
  }, [entryId, projectId, state, taskIds]);
  const busy = state === 'pending';

  return <>
    <button
      className={variant === 'chat' ? 'system-msg-review-btn system-msg-review-btn--review'
        : 'ai-overview-action-btn ai-overview-action-btn--cancel'}
      type="button" disabled={!enabled || !taskIds.length || busy || state === 'kept'} aria-busy={busy || undefined}
      title={KEEP_CURRENT_TITLE}
      onClick={keep}
    >
      {busy ? 'Keeping…' : state === 'kept' ? 'Kept current' : 'Keep current'}
    </button>
    {state === 'error' && <span className="ai-overview-action-error" role="alert">
      The current versions could not be kept. Try again.
    </span>}
  </>;
};
